import { useState } from "react";
import { t } from "i18next";
import InitSurveyResultsLogic, { trainingsWord, SurveyRating } from "./SurveyResults.logic";
import { formatDateTime } from "../../helpers/dateFormat";
import styles from "./SurveyResults.module.css";

const scoreColor = (score: number) => {
  if (score >= 8) return styles.scoreGood;
  if (score >= 5) return styles.scoreMid;
  return styles.scoreBad;
};

const isTrainerItem = (itemKey: string) => itemKey.startsWith("trainer:");

/** "5 treninga" ili "3 treninga kod trenera", null kad je broj nepoznat */
const countLabel = (rating: SurveyRating): string | null => {
  if (rating.trainingsCount === null) return null;
  const base = `${rating.trainingsCount} ${trainingsWord(rating.trainingsCount)}`;
  return isTrainerItem(rating.itemKey) ? `${base} kod trenera` : base;
};

const SurveyResultsComponent: React.FC = () => {
  const {
    surveys,
    filteredSurveys,
    loading,
    view,
    setView,
    excludeEnabled,
    setExcludeEnabled,
    excludeMax,
    setExcludeMax,
    byItem,
    suggestions,
    onBack,
  } = InitSurveyResultsLogic();

  // Pregled po pitanjima: sva pitanja su podrazumevano zatvorena,
  // otvara se jedno, vise ili sva
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const toggleItem = (itemKey: string) =>
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemKey)) next.delete(itemKey);
      else next.add(itemKey);
      return next;
    });

  if (loading) {
    return (
      <div className="page-container">
        <p className={styles.empty}>{`${t("Učitavanje...")}`}</p>
      </div>
    );
  }

  const items = byItem();
  const allSuggestions = suggestions();
  const visibleSurveys = filteredSurveys();

  return (
    <div className="page-container">
      <h1 className={styles.title}>{`${t("Rezultati upitnika")}`}</h1>
      <p className={styles.count}>
        {`${t("Popunjenih upitnika")}: ${surveys.length}`}
      </p>

      <div className={styles.filterCard}>
        <label className={styles.filterRow}>
          <input
            type="checkbox"
            checked={excludeEnabled}
            onChange={(e) => setExcludeEnabled(e.target.checked)}
          />
          <span>{`${t("Isključi ocene klijenata sa malim brojem treninga")}`}</span>
        </label>
        {excludeEnabled && (
          <div className={styles.filterDetail}>
            <span>{`${t("Isključi klijente koji imaju")}`}</span>
            <select
              value={excludeMax}
              aria-label={`${t("Najveći broj treninga koji se isključuje")}`}
              onChange={(e) => setExcludeMax(+e.target.value)}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span>{`${t("ili manje treninga")}`}</span>
          </div>
        )}
        {excludeEnabled && (
          <p className={styles.filterNote}>
            {`${t(
              "Za pitanja o treneru gleda se broj treninga kod tog trenera, a za opšta pitanja ukupan broj treninga klijenta.",
            )}`}
          </p>
        )}
      </div>

      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${view === "users" ? styles.tabActive : ""}`}
          onClick={() => setView("users")}
        >
          {`${t("Po korisnicima")}`}
        </button>
        <button
          type="button"
          className={`${styles.tab} ${view === "items" ? styles.tabActive : ""}`}
          onClick={() => setView("items")}
        >
          {`${t("Po pitanjima")}`}
        </button>
      </div>

      {surveys.length === 0 && (
        <p
          className={styles.empty}
        >{`${t("Još niko nije popunio upitnik.")}`}</p>
      )}

      {view === "users" &&
        visibleSurveys
          .filter((survey) => survey.ratings.length > 0 || survey.suggestions)
          .map((survey) => (
          <div key={survey.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.name}>
                {survey.clientName ?? `${t("Nepoznat klijent")}`}
                <span className={styles.countTag}>
                  {` (${survey.totalTrainings} ${trainingsWord(survey.totalTrainings)} ukupno)`}
                </span>
              </span>
              <span className={styles.date}>{formatDateTime(survey.date)}</span>
            </div>

            {survey.anonymous && (
              <div className={styles.anonNote}>
                {`${t(
                  "Označio je odgovor kao anoniman, ime ne treba pokazivati trenerima.",
                )}`}
              </div>
            )}

            {survey.ratings.map((rating) => (
              <div key={rating.itemKey} className={styles.row}>
                <span className={styles.rowLabel}>
                  {rating.label}
                  {countLabel(rating) && (
                    <span className={styles.countTag}>{` (${countLabel(rating)})`}</span>
                  )}
                </span>
                <span className={styles.rowRight}>
                  {rating.score !== null ? (
                    <span
                      className={`${styles.badge} ${scoreColor(rating.score)}`}
                    >
                      {rating.score}
                    </span>
                  ) : (
                    <span
                      className={styles.noScore}
                    >{`${t("nije ocenio")}`}</span>
                  )}
                </span>
                {rating.comment && (
                  <div className={styles.comment}>„{rating.comment}”</div>
                )}
              </div>
            ))}

            {survey.suggestions && (
              <div className={styles.suggestion}>
                <div className={styles.rowLabel}>{`${t("Predlog")}`}</div>
                <div className={styles.comment}>„{survey.suggestions}”</div>
              </div>
            )}
          </div>
        ))}

      {view === "items" && (
        <>
          {items.length > 0 && (
            <div className={styles.expandControls}>
              <button
                type="button"
                className={styles.expandButton}
                onClick={() =>
                  setOpenItems(new Set(items.map((i) => i.itemKey)))
                }
              >
                {`${t("Otvori sve")}`}
              </button>
              <button
                type="button"
                className={styles.expandButton}
                onClick={() => setOpenItems(new Set())}
              >
                {`${t("Zatvori sve")}`}
              </button>
            </div>
          )}

          {items.map((item) => {
            const isOpen = openItems.has(item.itemKey);
            return (
              <div key={item.itemKey} className={styles.card}>
                <div
                  className={`${styles.cardHeader} ${styles.itemToggle} ${isOpen ? styles.itemToggleSticky : ""}`}
                  role="button"
                  aria-expanded={isOpen}
                  onClick={() => toggleItem(item.itemKey)}
                >
                  <span className={styles.name}>
                    <span className={styles.chevron}>{isOpen ? "▾" : "▸"}</span>
                    {item.label}
                  </span>
                  <span className={styles.rowRight}>
                    {item.average !== null ? (
                      <span
                        className={`${styles.badge} ${scoreColor(item.average)}`}
                      >
                        {item.average.toFixed(1)}
                      </span>
                    ) : (
                      <span
                        className={styles.noScore}
                      >{`${t("nema ocena")}`}</span>
                    )}
                  </span>
                </div>
                <div className={styles.date}>
                  {`${t("Ocenilo")}: ${item.count}`}
                </div>

                {isOpen && item.entries.length > 0 && (
                  <div className={styles.scoreList}>
                    {item.entries.map((entry, index) => (
                      <div key={index} className={styles.scoreEntry}>
                        {entry.score !== null ? (
                          <span
                            className={`${styles.badge} ${scoreColor(entry.score)}`}
                          >
                            {entry.score}
                          </span>
                        ) : (
                          <span className={styles.noScore}>
                            {`${t("bez ocene")}`}
                          </span>
                        )}
                        <span className={styles.scoreEntryText}>
                          {entry.clientName ?? `${t("Nepoznat")}`}
                          {entry.trainingsCount !== null &&
                            `, ${entry.trainingsCount} ${trainingsWord(entry.trainingsCount)}${isTrainerItem(item.itemKey) ? " kod trenera" : ""}`}
                          {entry.anonymous && (
                            <span className={styles.anonTag}>
                              {` ${t("(anonimno prema trenerima)")}`}
                            </span>
                          )}
                        </span>
                        {entry.comment && (
                          <div className={styles.comment}>„{entry.comment}”</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {allSuggestions.length > 0 && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <span
                  className={styles.name}
                >{`${t("Predlozi za unapređenje")}`}</span>
              </div>
              <div className={styles.commentList}>
                {allSuggestions.map((s, index) => (
                  <div key={index} className={styles.commentBlock}>
                    <span className={styles.commentAuthor}>
                      {s.clientName ?? `${t("Nepoznat")}`}
                      {s.anonymous && (
                        <span className={styles.anonTag}>
                          {` ${t("(anonimno prema trenerima)")}`}
                        </span>
                      )}
                    </span>
                    <div className={styles.comment}>„{s.text}”</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <button
        type="button"
        className="btn-secondary"
        style={{ marginTop: 20 }}
        onClick={onBack}
      >
        {`${t("Nazad")}`}
      </button>
    </div>
  );
};

export default SurveyResultsComponent;
